import { createFileRoute, useNavigate, useRouterState } from "@tanstack/react-router";
import { requireAdminRoute } from "@/lib/require-admin-route";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { apiFetch, ApiRequestError } from "@/lib/api-client";
import { humanizeApiError } from "@/lib/form-errors";
import { useToast } from "@/components/ui/Toast";
import { useTopbar } from "@/components/chrome/use-topbar";
import { radarLenderUrl } from "@/lib/radar";
import { ExternalLinkIcon } from "@/components/ExternalLinkIcon";
import { LenderFormSheet } from "@/components/settings/LenderFormSheet";
import { ScanConfirmDialog } from "@/components/settings/ScanConfirmDialog";
import { ResyncResultsModal } from "@/components/settings/ResyncResultsModal";
import { startLenderResync } from "@/lib/runs";

interface Lender {
  id: string;
  name: string;
  kind: "whitelist" | "blacklist";
  reason: string | null;
  enabled: boolean;
  aliases: { id: string; alias: string }[];
}
interface BorrowerSkip {
  id: string;
  displayName: string;
  normalizedName: string;
  reason: string | null;
}
interface LendersResponse {
  lenders: Lender[];
  borrowerBlacklist: BorrowerSkip[];
}

export const Route = createFileRoute("/_app/settings/lenders")({
  beforeLoad: requireAdminRoute,
  component: LendersPage,
});

function LendersPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { data, isLoading, error } = useQuery<LendersResponse>({
    queryKey: ["settings", "lenders"],
    queryFn: () => apiFetch<LendersResponse>("/api/settings/lenders"),
  });

  // Shared error handler: 401 routes to login, everything else toasts a
  // readable summary (F-06-02 / F-06-07 / F-08-04).
  const onMutationError = (err: unknown) => {
    if (err instanceof ApiRequestError && err.status === 401) {
      void navigate({ to: `/login?redirect=${encodeURIComponent(pathname)}` });
      return;
    }
    toast.error(humanizeApiError(err).summary);
  };

  const patchLender = useMutation({
    mutationFn: (input: { id: string; name?: string; enabled?: boolean }) =>
      apiFetch<{ ok: true }>(`/api/settings/lenders/lender/${input.id}`, {
        method: "PATCH",
        body: JSON.stringify({ name: input.name, enabled: input.enabled }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["settings", "lenders"] }),
    onError: onMutationError,
  });

  const removeLender = useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ ok: true }>(`/api/settings/lenders/lender/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["settings", "lenders"] }),
    onError: onMutationError,
  });

  const addBorrower = useMutation({
    mutationFn: (input: { displayName: string; reason?: string }) =>
      apiFetch<{ id: string }>("/api/settings/lenders/borrower-blacklist", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["settings", "lenders"] }),
    onError: onMutationError,
  });

  const removeBorrower = useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ ok: true }>(`/api/settings/lenders/borrower-blacklist/${id}`, {
        method: "DELETE",
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["settings", "lenders"] }),
    onError: onMutationError,
  });

  const [tab, setTab] = useState<"whitelist" | "borrowers">("whitelist");
  // Borrower-add form fields (whitelist add/edit now lives in LenderFormSheet).
  const [newName, setNewName] = useState("");
  const [newReason, setNewReason] = useState("");
  // Add/Edit sheet + post-save resync flow.
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Lender | null>(null);
  const [confirm, setConfirm] = useState<{
    lenderId: string;
    lenderName: string;
    scopeNames: string[];
    aliasIds?: string[];
  } | null>(null);
  const [activeRun, setActiveRun] = useState<{
    runId: string;
    lenderName: string;
    scopeNames: string[];
  } | null>(null);

  function handleSaved(r: {
    lenderId: string;
    lenderName: string;
    isNewLender: boolean;
    newAliases: { id: string; alias: string }[];
  }) {
    setFormOpen(false);
    setEditing(null);
    // Prefer the name the form just saved (the lenders cache invalidation is
    // async, so a brand-new lender isn't in `data` yet).
    const lenderName = r.lenderName.trim()
      ? r.lenderName.trim()
      : (data?.lenders.find((x) => x.id === r.lenderId)?.name ?? "Lender");
    // New lender → whole-lender scope (no aliasIds). Edit with new aliases → diff scope.
    if (r.isNewLender) {
      setConfirm({ lenderId: r.lenderId, lenderName, scopeNames: [], aliasIds: undefined });
    } else if (r.newAliases.length > 0) {
      setConfirm({
        lenderId: r.lenderId,
        lenderName,
        scopeNames: r.newAliases.map((a) => a.alias),
        aliasIds: r.newAliases.map((a) => a.id),
      });
    }
    // else: rename/toggle/removal only → no scan prompt.
  }

  async function handleConfirm() {
    if (!confirm) return;
    try {
      const { runId } = await startLenderResync({
        lenderId: confirm.lenderId,
        aliasIds: confirm.aliasIds,
      });
      setActiveRun({ runId, lenderName: confirm.lenderName, scopeNames: confirm.scopeNames });
    } catch (err) {
      onMutationError(err);
    } finally {
      setConfirm(null);
    }
  }

  const { setTopbar } = useTopbar();
  useEffect(() => {
    setTopbar({
      crumbs: [
        { label: "Operations" },
        { label: "Settings", to: "/settings" },
        { label: "Lenders" },
      ],
      rightSlot: null,
    });
    return () => setTopbar({ crumbs: [], rightSlot: null });
  }, [setTopbar]);

  if (isLoading) return <p>Loading…</p>;
  if (error) return <p className="text-red-400">Failed to load lenders.</p>;
  if (!data) return null;

  const filtered = tab === "borrowers" ? null : data.lenders.filter((l) => l.kind === tab);
  const counts = {
    whitelist: data.lenders.filter((l) => l.kind === "whitelist").length,
    borrowers: data.borrowerBlacklist.length,
  };

  return (
    <div className="space-y-6">
      <div className="border-b border-rule pb-5">
        <h1 className="mb-1 text-3xl font-semibold tracking-tight text-ink">Lenders</h1>
        <p className="text-sm text-muted">
          Competitor whitelist for Priority A re-underwriting, plus a borrower skip-list to exclude
          from the daily queue.
        </p>
      </div>

      <div className="inline-flex gap-0.5 rounded-lg bg-paper-2 p-0.5 text-sm">
        {(["whitelist", "borrowers"] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={`rounded-md px-4 py-2 text-[12.5px] font-semibold transition ${
              tab === t ? "bg-surface text-ink shadow-sm" : "text-muted hover:text-ink"
            }`}
          >
            {t === "whitelist" ? "Whitelist" : "Borrower skip-list"}{" "}
            <span
              className={`ml-1 inline-flex min-w-[18px] justify-center rounded-full px-1.5 text-[10px] font-bold ${
                tab === t ? "bg-ink text-surface" : "bg-paper-3 text-muted"
              }`}
            >
              {counts[t]}
            </span>
          </button>
        ))}
      </div>

      {tab === "whitelist" ? (
        <div className="flex gap-3 rounded-lg border border-rule bg-gold-soft px-4 py-3">
          <svg
            className="mt-0.5 h-4 w-4 shrink-0 text-gold-dark"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM9 9a1 1 0 012 0v4a1 1 0 11-2 0V9zm1-4a1 1 0 100 2 1 1 0 000-2z"
              clipRule="evenodd"
            />
          </svg>
          <p className="text-[13px] leading-relaxed text-ink/80">
            <span className="font-semibold text-ink">Only lenders listed here are pulled.</span>{" "}
            Each daily scan queries our data provider by name for every lender below — plus any
            aliases — and nothing else. A lender that isn't listed is never fetched.
          </p>
        </div>
      ) : null}

      {tab === "borrowers" ? (
        <form
          className="flex flex-col gap-2 md:flex-row"
          onSubmit={(e) => {
            e.preventDefault();
            if (!newName.trim()) return;
            addBorrower.mutate({
              displayName: newName.trim(),
              reason: newReason.trim() || undefined,
            });
            setNewName("");
            setNewReason("");
          }}
        >
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Borrower / entity name"
            className="flex-1 rounded border border-rule-strong bg-surface px-3 py-2 text-sm text-ink placeholder:text-muted-2 focus:outline-none focus:ring-1 focus:ring-gold"
          />
          <input
            value={newReason}
            onChange={(e) => setNewReason(e.target.value)}
            placeholder="Reason (optional)"
            className="flex-1 rounded border border-rule-strong bg-surface px-3 py-2 text-sm text-ink placeholder:text-muted-2 focus:outline-none focus:ring-1 focus:ring-gold"
          />
          <button
            type="submit"
            className="rounded bg-gold px-4 py-2.5 text-sm font-semibold text-ink md:py-2"
          >
            Add
          </button>
        </form>
      ) : (
        <button
          type="button"
          onClick={() => {
            setEditing(null);
            setFormOpen(true);
          }}
          className="rounded bg-gold px-4 py-2.5 text-sm font-semibold text-ink md:py-2"
        >
          Add lender
        </button>
      )}

      {tab === "borrowers" ? (
        <ul className="divide-y divide-rule">
          {data.borrowerBlacklist.map((b) => (
            <li key={b.id} className="flex items-center justify-between py-2 text-sm">
              <div>
                <div className="font-medium">{b.displayName}</div>
                <div className="text-xs text-muted">
                  Match key: <code>{b.normalizedName}</code>
                  {b.reason ? ` · ${b.reason}` : null}
                </div>
              </div>
              <button
                type="button"
                onClick={() => removeBorrower.mutate(b.id)}
                className="rounded border border-rule-strong bg-surface px-3 py-2.5 text-xs text-ink hover:bg-paper-2 md:py-1"
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <ul className="divide-y divide-rule">
          {filtered?.map((l) => (
            <li
              key={l.id}
              className={`flex items-center justify-between gap-3 py-2 text-sm ${
                l.enabled ? "" : "opacity-50"
              }`}
            >
              <div className="min-w-0 flex-1">
                <div className="max-w-full truncate font-medium">{l.name}</div>
                {l.reason ? <div className="text-xs text-muted">{l.reason}</div> : null}
                {l.aliases.length > 0 ? (
                  <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                    {l.aliases.map((a) => (
                      <span
                        key={a.id}
                        className="inline-flex items-center rounded-full border border-rule bg-gold-soft px-2 py-0.5 text-[11px] font-medium text-ink/80"
                      >
                        {a.alias}
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>
              <div className="flex shrink-0 items-center gap-3">
                <button
                  type="button"
                  onClick={() =>
                    setConfirm({
                      lenderId: l.id,
                      lenderName: l.name,
                      scopeNames: [],
                      aliasIds: undefined,
                    })
                  }
                  className="rounded border border-gold-dark bg-surface px-3 py-2.5 text-xs text-gold-dark hover:bg-paper-2 md:py-1"
                >
                  Resync
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setEditing(l);
                    setFormOpen(true);
                  }}
                  className="rounded border border-rule-strong bg-surface px-3 py-2.5 text-xs text-ink hover:bg-paper-2 md:py-1"
                >
                  Edit
                </button>
                <a
                  href={radarLenderUrl(l.name)}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="View lender on Private Lender Radar"
                  className="inline-flex items-center gap-1 text-xs text-muted hover:text-ink hover:underline"
                >
                  Radar
                  <ExternalLinkIcon className="inline-block h-3 w-3 opacity-60" />
                </a>
                <button
                  type="button"
                  role="switch"
                  aria-checked={l.enabled}
                  aria-label={l.enabled ? "Disable lender" : "Enable lender"}
                  onClick={() => patchLender.mutate({ id: l.id, enabled: !l.enabled })}
                  disabled={patchLender.isPending}
                  className={`relative h-5 w-9 shrink-0 rounded-full transition-colors disabled:opacity-50 ${
                    l.enabled ? "bg-gold" : "bg-paper-3"
                  }`}
                >
                  <span
                    className={`absolute left-0 top-0.5 h-4 w-4 rounded-full bg-ink transition-transform ${
                      l.enabled ? "translate-x-4" : "translate-x-0.5"
                    }`}
                  />
                </button>
                <button
                  type="button"
                  onClick={() => removeLender.mutate(l.id)}
                  className="rounded border border-rule-strong bg-surface px-3 py-2.5 text-xs text-ink hover:bg-paper-2 md:py-1"
                >
                  Remove
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <LenderFormSheet
        open={formOpen}
        lender={editing}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
        onSaved={handleSaved}
      />
      <ScanConfirmDialog
        open={confirm !== null}
        lenderName={confirm?.lenderName ?? ""}
        scopeNames={confirm?.scopeNames ?? []}
        onConfirm={() => void handleConfirm()}
        onLater={() => setConfirm(null)}
      />
      {activeRun ? (
        <ResyncResultsModal
          open={true}
          runId={activeRun.runId}
          lenderName={activeRun.lenderName}
          scopeNames={activeRun.scopeNames}
          onClose={() => setActiveRun(null)}
        />
      ) : null}
    </div>
  );
}
